import { ClientBalanceEntry } from 'entities';

import { getFirst } from 'utils/array';

import { BaseInvoiceProps, InvoiceCreationProps } from './types';
import { arePropertiesTruthy } from 'utils/object';

function calculateIncrease(value: string, percentage: string): number {
  const decimal = Number(percentage) / 100;

  return decimal * Number(value);
}

export function calculatePercentageIncrease(value: string, percentage: string | undefined): number {
  if (!percentage || !value) return 0;

  const fixed = calculateIncrease(value, percentage).toFixed(2);

  return Number(fixed);
}

export function calculateTotalValue(
  value: string,
  additionalPercentage: InvoiceCreationProps['additionalPercentage'],
  customValueResolver?: (value: string, newValue: string) => number,
): number {
  if (!value) return 0;

  if (customValueResolver) return customValueResolver(value, additionalPercentage?.value as string);

  const base = Number(value);

  const total = additionalPercentage?.value
    ? base + calculateIncrease(value, additionalPercentage.value)
    : base;

  const fixed = total.toFixed(2);

  return Number(fixed);
}

export function extractFixedInvoiceData(
  projectInvoices: BaseInvoiceProps[],
): Omit<BaseInvoiceProps, 'value' | 'name' | 'id'> {
  const { client, currency, country, clientName } = getFirst(projectInvoices) || {
    client: null,
    clientName: '',
    currency: '',
    country: 'USA',
  };

  return { client, currency, country, clientName };
}

export function validateCanCreate(state: InvoiceCreationProps): boolean {
  const { dateRequest, additionalPercentage } = state;

  const requiredProps: (keyof InvoiceCreationProps)[] = [
    'description',
    'taxId',
    'address',
    'companyName',
    'clientPM',
    'poNumber',
  ];

  const canCreate = [
    requiredProps.every((prop) => state[prop]),
    state.emails.length,
    additionalPercentage ? arePropertiesTruthy(additionalPercentage) : true,
    arePropertiesTruthy(dateRequest),
  ].every(Boolean);

  return canCreate;
}

export function calculateBalancesTotal(balances: ClientBalanceEntry[]): number {
  const total = balances.reduce(
    (acc, next) =>
      next.type === 'OVERPAYMENT' ? acc - Number(next.delta) : acc + Number(next.delta),
    0,
  );

  return total;
}

export function verifyIfBalanceCanBeAdded({
  balance,
  state,
  total,
}: {
  state: InvoiceCreationProps;
  total: string;
  balance: ClientBalanceEntry;
}): boolean {
  const alreadyAdded = state.clientBalances.find(({ id }) => id === balance.id);

  if (alreadyAdded) return false;

  const totalWithBalances = Number(total) + calculateBalancesTotal(state.clientBalances);

  const withNewBalance =
    balance.type === 'OVERPAYMENT'
      ? totalWithBalances - Number(balance.delta)
      : totalWithBalances + Number(balance.delta);

  return withNewBalance > 0;
}
